import { queryOptions, useQuery } from '@tanstack/react-query';

import type {
  TLSalesforceCredentialsRequest,
  TLSalesforceCredentialsResponse,
  TLSAPCredentialsRequest,
  TLSAPCredentialsResponse,
} from '../gen';
import { createSalesforceCrmCredentials, createSapErpCredentials, getRetrieveUserErpCredentialsQueryKey } from '../gen';

import { useMutationNext } from '@/query';
import type { MutationOptionsNext } from '@/query/types';
import type { TLAIDeleteCredentialsRequest, TLAIUserCredentialsResponse } from '@/types/api';
import { getApiClient } from '@/utils/api-client';
import type { QueryOptions } from '@/utils/types';

export const userCredentialsQueryOptions = (options?: QueryOptions<TLAIUserCredentialsResponse>) =>
  queryOptions({
    queryKey: ['user', 'credentials'],
    queryFn: async () => (await getApiClient()).get<TLAIUserCredentialsResponse>('/user-credentials'),
    ...options,
  });

export const useUserCredentialsQuery = (options?: QueryOptions<TLAIUserCredentialsResponse>) => {
  return useQuery(userCredentialsQueryOptions(options));
};

export const useCreateSAPCredentialsMutation = (
  options?: MutationOptionsNext<TLSAPCredentialsResponse, Error, TLSAPCredentialsRequest>,
) => {
  return useMutationNext({
    mutationFn: (input: TLSAPCredentialsRequest) => createSapErpCredentials(input),
    // The connectors legacy drawer reads ERP logins through the generated
    // query; refresh it so its sign-in state flips without a remount.
    invalidateQueries: [getRetrieveUserErpCredentialsQueryKey()],
    ...options,
  });
};

export const useCreateSalesforceCredentialsMutation = (
  options?: MutationOptionsNext<TLSalesforceCredentialsResponse, Error, TLSalesforceCredentialsRequest>,
) => {
  return useMutationNext({
    mutationFn: (input: TLSalesforceCredentialsRequest) => createSalesforceCrmCredentials(input),
    invalidateQueries: [getRetrieveUserErpCredentialsQueryKey()],
    ...options,
  });
};

export const useDeleteCredentialsMutation = (
  options?: MutationOptionsNext<unknown, Error, TLAIDeleteCredentialsRequest>,
) => {
  return useMutationNext({
    mutationFn: async (input: TLAIDeleteCredentialsRequest) =>
      (await getApiClient()).delete('/user-credentials/delete', {
        urlParams: { path: [input.credentials_id] },
      }),
    invalidateQueries: [getRetrieveUserErpCredentialsQueryKey()],
    ...options,
  });
};
